import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { CommissionRuleEditor, type Rule } from "../components/CommissionRuleEditor";
import type { Plan, Product, ProductStatus } from "../lib/types";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  PageHeader,
  Select,
  Table,
  TableWrap,
  Td,
  Th,
  centsFromInput,
  formatMoney,
  inputFromCents,
} from "../components/ui";

const DEFAULT_POLICY = {
  heartbeatSeconds: 1800,
  tokenLifetimeSeconds: 3600,
  stages: [
    { afterSeconds: 0, mode: "normal" },
    { afterSeconds: 604800, mode: "warn" },
    { afterSeconds: 1209600, mode: "checkout_off" },
    { afterSeconds: 1814400, mode: "blocked" },
  ],
};

export function Catalogue() {
  const queryClient = useQueryClient();
  const [editingProduct, setEditingProduct] = useState<Product | "new" | null>(null);
  const [editingPlan, setEditingPlan] = useState<Plan | "new" | null>(null);
  const [confirmingProduct, setConfirmingProduct] = useState<string | null>(null);
  const [confirmingPlan, setConfirmingPlan] = useState<string | null>(null);

  const products = useQuery({
    queryKey: ["products"],
    queryFn: () => api<Product[]>("/api/v1/admin/products"),
  });
  const plans = useQuery({
    queryKey: ["plans"],
    queryFn: () => api<Plan[]>("/api/v1/admin/plans"),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["products"] });
    void queryClient.invalidateQueries({ queryKey: ["plans"] });
  };

  const saveProduct = useMutation({
    mutationFn: (input: { id?: string; body: Record<string, unknown> }) =>
      input.id === undefined
        ? api("/api/v1/admin/products", { method: "POST", body: input.body })
        : api(`/api/v1/admin/products/${input.id}`, { method: "PATCH", body: input.body }),
    onSuccess: () => {
      setEditingProduct(null);
      invalidate();
    },
  });

  const savePlan = useMutation({
    mutationFn: (input: { id?: string; body: Record<string, unknown> }) =>
      input.id === undefined
        ? api("/api/v1/admin/plans", { method: "POST", body: input.body })
        : api(`/api/v1/admin/plans/${input.id}`, { method: "PATCH", body: input.body }),
    onSuccess: () => {
      setEditingPlan(null);
      invalidate();
    },
  });

  /** Publishing a price and selling unattended are separate switches. */
  const togglePlan = useMutation({
    mutationFn: (input: { id: string; body: Record<string, unknown> }) =>
      api(`/api/v1/admin/plans/${input.id}`, { method: "PATCH", body: input.body }),
    onSuccess: invalidate,
  });

  const deleteProduct = useMutation({
    mutationFn: (id: string) => api(`/api/v1/admin/products/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setConfirmingProduct(null);
      invalidate();
    },
  });

  const deletePlan = useMutation({
    mutationFn: (id: string) => api(`/api/v1/admin/plans/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setConfirmingPlan(null);
      invalidate();
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Products and plans"
        lede="What can be licensed, and the terms each licence inherits."
      />

      <Card
        title="Products"
        action={
          <Button onClick={() => setEditingProduct((value) => (value === null ? "new" : null))}>
            {editingProduct === null ? "Add product" : "Cancel"}
          </Button>
        }
      >
        {editingProduct !== null && (
          <ProductForm
            key={editingProduct === "new" ? "new" : editingProduct.id}
            product={editingProduct === "new" ? null : editingProduct}
            pending={saveProduct.isPending}
            error={saveProduct.isError ? (saveProduct.error as Error).message : null}
            onSubmit={(body) =>
              saveProduct.mutate({
                ...(editingProduct === "new" ? {} : { id: editingProduct.id }),
                body,
              })
            }
          />
        )}

        {(products.data?.length ?? 0) === 0 ? (
          <EmptyState>No products yet. A product is what a licence key belongs to.</EmptyState>
        ) : (
          <>
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Name</Th>
                  <Th>Slug</Th>
                  <Th>Code</Th>
                  <Th>Status</Th>
                  <Th>One-time price</Th>
                  <Th>Commission</Th>
                  <Th>Plans</Th>
                  <Th>Licences</Th>
                  <Th className="sr-only">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {(products.data ?? []).map((product) => (
                  <tr key={product.id} className="border-b border-hairline last:border-0">
                    <Td label="Name">{product.name}</Td>
                    <Td label="Slug" className="tabular text-muted">
                      {product.slug}
                    </Td>
                    <Td label="Code" className="tabular text-accent">
                      {product.code}
                    </Td>
                    <Td label="Status">
                      <ProductStatusBadge status={product.status} />
                    </Td>
                    <Td label="One-time price" className="tabular">
                      {formatMoney(product.oneTimePriceCents, product.currency)}
                    </Td>
                    <Td label="Commission" className="tabular text-muted">
                      {product.commissionCents === 0 ? (
                        <span className="text-dim">None</span>
                      ) : (
                        formatMoney(product.commissionCents, product.currency)
                      )}
                    </Td>
                    <Td label="Plans" className="tabular text-muted">
                      {product._count?.plans ?? 0}
                    </Td>
                    <Td label="Licences" className="tabular text-muted">
                      {product._count?.licenses ?? 0}
                    </Td>
                    <Td label="Actions">
                      {confirmingProduct === product.id ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs text-muted">Delete {product.name}?</span>
                          <Button
                            variant="danger"
                            disabled={deleteProduct.isPending}
                            onClick={() => deleteProduct.mutate(product.id)}
                          >
                            Delete
                          </Button>
                          <Button onClick={() => setConfirmingProduct(null)}>Keep</Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button variant="ghost" onClick={() => setEditingProduct(product)}>
                            Edit
                          </Button>
                          <Button variant="ghost" onClick={() => setConfirmingProduct(product.id)}>
                            Delete
                          </Button>
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
          {deleteProduct.isError && (
            <div className="px-4 pb-4">
              <ErrorNote>{(deleteProduct.error as Error).message}</ErrorNote>
            </div>
          )}
          </>
        )}
      </Card>

      <Card
        title="Plans"
        action={
          <Button
            onClick={() => setEditingPlan((value) => (value === null ? "new" : null))}
            disabled={(products.data?.length ?? 0) === 0}
          >
            {editingPlan === null ? "Add plan" : "Cancel"}
          </Button>
        }
      >
        {editingPlan !== null && (
          <PlanForm
            key={editingPlan === "new" ? "new" : editingPlan.id}
            products={products.data ?? []}
            plan={editingPlan === "new" ? null : editingPlan}
            pending={savePlan.isPending}
            error={savePlan.isError ? (savePlan.error as Error).message : null}
            onSubmit={(body) =>
              savePlan.mutate({ ...(editingPlan === "new" ? {} : { id: editingPlan.id }), body })
            }
          />
        )}

        {(plans.data?.length ?? 0) === 0 ? (
          <EmptyState>No plans yet.</EmptyState>
        ) : (
          <>
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Product</Th>
                  <Th>Plan</Th>
                  <Th>Interval</Th>
                  <Th>Seats</Th>
                  <Th>Build</Th>
                  <Th>Monthly</Th>
                  <Th>Yearly</Th>
                  <Th>Launch price</Th>
                  <Th>Grace</Th>
                  <Th>Licences</Th>
                  <Th>On pricing page</Th>
                  <Th>Sold on the site</Th>
                  <Th className="sr-only">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {(plans.data ?? []).map((plan) => (
                  <tr key={plan.id} className="border-b border-hairline last:border-0">
                    <Td label="Product" className="text-muted">
                      {plan.product?.name ?? "—"}
                    </Td>
                    <Td label="Plan">{plan.name}</Td>
                    <Td label="Interval" className="text-muted">
                      {plan.interval}
                    </Td>
                    <Td label="Seats" className="tabular text-muted">
                      {plan.maxActivations}
                    </Td>
                    <Td label="Build" className="tabular text-muted">
                      {formatMoney(plan.oneTimePriceCents ?? null, plan.currency)}
                    </Td>
                    <Td label="Monthly" className="tabular text-muted">
                      {formatMoney(plan.priceMonthlyCents ?? null, plan.currency)}
                    </Td>
                    <Td label="Yearly" className="tabular text-muted">
                      {formatMoney(plan.priceYearlyCents ?? null, plan.currency)}
                    </Td>
                    <Td label="Launch price" className="tabular text-muted">
                      <LaunchPriceCell plan={plan} />
                    </Td>
                    <Td label="Grace" className="tabular text-muted">
                      {plan.maintenanceGraceMonths == null ? "—" : `${plan.maintenanceGraceMonths} mo`}
                    </Td>
                    <Td label="Licences" className="tabular text-muted">
                      {plan._count?.licenses ?? 0}
                    </Td>
                    <Td label="On pricing page">
                      <Button
                        variant="ghost"
                        disabled={togglePlan.isPending}
                        onClick={() =>
                          togglePlan.mutate({
                            id: plan.id,
                            body: { publicVisible: !plan.publicVisible },
                          })
                        }
                      >
                        {plan.publicVisible ? "Published" : "Hidden"}
                      </Button>
                    </Td>
                    <Td label="Sold on the site">
                      <SellToggle
                        plan={plan}
                        pending={togglePlan.isPending}
                        onToggle={() =>
                          togglePlan.mutate({ id: plan.id, body: { selfServe: !plan.selfServe } })
                        }
                      />
                    </Td>
                    <Td label="Actions">
                      {confirmingPlan === plan.id ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs text-muted">Delete {plan.name}?</span>
                          <Button
                            variant="danger"
                            disabled={deletePlan.isPending}
                            onClick={() => deletePlan.mutate(plan.id)}
                          >
                            Delete
                          </Button>
                          <Button onClick={() => setConfirmingPlan(null)}>Keep</Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button variant="ghost" onClick={() => setEditingPlan(plan)}>
                            Edit
                          </Button>
                          <Button variant="ghost" onClick={() => setConfirmingPlan(plan.id)}>
                            Delete
                          </Button>
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
          {deletePlan.isError && (
            <div className="px-4 pb-4">
              <ErrorNote>{(deletePlan.error as Error).message}</ErrorNote>
            </div>
          )}
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * Slug and code are shown but never editable after creation. Both are baked
 * into keys already issued, so changing either would leave a customer holding a
 * key whose product no longer matches it.
 */
function ProductForm({
  product,
  pending,
  error,
  onSubmit,
}: {
  product: Product | null;
  pending: boolean;
  error: string | null;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [slug, setSlug] = useState(product?.slug ?? "");
  const [code, setCode] = useState(product?.code ?? "");
  const [name, setName] = useState(product?.name ?? "");
  const [price, setPrice] = useState(inputFromCents(product?.oneTimePriceCents));
  const [commission, setCommission] = useState(
    product == null || product.commissionCents === 0 ? "" : inputFromCents(product.commissionCents),
  );
  const [status, setStatus] = useState<ProductStatus>(product?.status ?? "draft");
  const [summary, setSummary] = useState(product?.publicSummary ?? "");
  const [timeline, setTimeline] = useState(product?.publicTimeline ?? "");
  const [badge, setBadge] = useState(product?.publicBadge ?? "");
  const [order, setOrder] = useState(String(product?.displayOrder ?? 0));

  const isNew = product === null;

  return (
    <form
      className="space-y-4 border-b border-hairline p-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          ...(isNew ? { slug, code: code.toUpperCase() } : {}),
          name,
          oneTimePriceCents: centsFromInput(price) ?? null,
          commissionCents: centsFromInput(commission) ?? 0,
          currency: "USD",
          status,
          publicSummary: summary.trim() === "" ? null : summary.trim(),
          publicTimeline: timeline.trim() === "" ? null : timeline.trim(),
          publicBadge: badge.trim() === "" ? null : badge.trim(),
          displayOrder: Number(order) || 0,
        });
      }}
    >
      <div className="grid gap-4 md:grid-cols-3">
        <Field label="Name">
          <Input value={name} required onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field
          label="Slug"
          hint={isNew ? "Used by the SDK. Cannot be changed later." : "Permanent."}
        >
          <Input
            value={slug}
            required
            readOnly={!isNew}
            className={isNew ? "tabular" : "tabular text-dim"}
            onChange={(event) => setSlug(event.target.value.toLowerCase())}
          />
        </Field>
        <Field
          label="Code"
          hint={
            isNew
              ? "Four symbols, embedded in every key."
              : (product?._count?.licenses ?? 0) > 0
                ? "In every key issued. Fixed now."
                : "Still changeable — nothing has been issued yet."
          }
        >
          <Input
            value={code}
            required
            maxLength={4}
            readOnly={!isNew && (product?._count?.licenses ?? 0) > 0}
            className={
              !isNew && (product?._count?.licenses ?? 0) > 0
                ? "tabular uppercase text-dim"
                : "tabular uppercase"
            }
            onChange={(event) => setCode(event.target.value.toUpperCase())}
          />
        </Field>

        <Field label="One-time price" hint="What the product itself costs, before maintenance.">
          <Input
            type="number"
            min={0}
            step="0.01"
            value={price}
            placeholder="0.00"
            onChange={(event) => setPrice(event.target.value)}
          />
        </Field>

        {/* Copied onto each order at sale time, so editing it later changes
            what future sales pay and never what someone has already earned. */}
        <Field
          label="Marketer commission"
          hint="Fixed amount per sale. Leave empty if this product is never sold on commission."
        >
          <Input
            type="number"
            min={0}
            step="0.01"
            value={commission}
            placeholder="0.00"
            onChange={(event) => setCommission(event.target.value)}
          />
        </Field>

        {/* Two gates decide what the website shows: this, and each plan's own
            "on pricing page". A product that is not public hides all of them. */}
        <Field label="Status" hint={STATUS_HINTS[status]}>
          <Select
            value={status}
            onChange={(event) => setStatus(event.target.value as ProductStatus)}
          >
            <option value="draft">Draft</option>
            <option value="public">Public</option>
            <option value="internal">Internal</option>
            <option value="disabled">Disabled</option>
          </Select>
        </Field>

        <Field label="Order on the site" hint="Lower shows first.">
          <Input
            type="number"
            min={0}
            value={order}
            onChange={(event) => setOrder(event.target.value)}
          />
        </Field>

        <Field label="Badge" hint={'e.g. "Most chosen". Leave empty for none.'}>
          <Input value={badge} onChange={(event) => setBadge(event.target.value)} />
        </Field>

        <Field
          label="Summary"
          hint="One line, for cards and the nav. The long copy lives in the site repo."
        >
          <Input
            value={summary}
            maxLength={200}
            onChange={(event) => setSummary(event.target.value)}
          />
        </Field>

        <Field label="Typical delivery" hint={'Shown on the site, e.g. "2-3 days".'}>
          <Input value={timeline} onChange={(event) => setTimeline(event.target.value)} />
        </Field>
      </div>

      {error !== null && <ErrorNote>{error}</ErrorNote>}

      <Button type="submit" variant="primary" disabled={pending}>
        {pending ? "Saving…" : isNew ? "Create product" : "Save product"}
      </Button>
    </form>
  );
}

function PlanForm({
  products,
  plan,
  pending,
  error,
  onSubmit,
}: {
  products: Product[];
  /** Null or omitted means create. Present means edit that plan. */
  plan?: Plan | null;
  pending: boolean;
  error: string | null;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const isEditing = plan != null;

  const [productId, setProductId] = useState(plan?.productId ?? products[0]?.id ?? "");
  const [name, setName] = useState(plan?.name ?? "");
  const [maxActivations, setMaxActivations] = useState(plan?.maxActivations ?? 2);
  const [build, setBuild] = useState(inputFromCents(plan?.oneTimePriceCents));
  const [monthly, setMonthly] = useState(inputFromCents(plan?.priceMonthlyCents));
  const [yearly, setYearly] = useState(inputFromCents(plan?.priceYearlyCents));
  const [compareAt, setCompareAt] = useState(inputFromCents(plan?.compareAtPriceCents));
  const [promoPrice, setPromoPrice] = useState(inputFromCents(plan?.promoPriceCents));
  const [promoLimit, setPromoLimit] = useState(
    plan?.promoQuantityLimit == null ? "" : String(plan.promoQuantityLimit),
  );
  const [graceMonths, setGraceMonths] = useState(
    plan?.maintenanceGraceMonths == null ? "" : String(plan.maintenanceGraceMonths),
  );
  // Commission is a rule now, not an amount — see CommissionRuleEditor.
  const [commissionRule, setCommissionRule] = useState<Rule | null>(
    (plan?.commissionRule as Rule | null | undefined) ?? null,
  );
  const [floorPrice, setFloorPrice] = useState(inputFromCents(plan?.floorPriceCents));
  const [floorMonthly, setFloorMonthly] = useState(inputFromCents(plan?.floorMonthlyCents));
  const [requiresLicense, setRequiresLicense] = useState(plan?.requiresLicense ?? true);
  const [selfServe, setSelfServe] = useState(plan?.selfServe ?? false);
  const [provisioning, setProvisioning] = useState<string>(plan?.provisioning ?? "on_payment");

  const monthlyCents = centsFromInput(monthly);
  const yearlyCents = centsFromInput(yearly);
  const recurring = monthlyCents !== undefined || yearlyCents !== undefined;

  const compareAtCents = centsFromInput(compareAt);
  const promoPriceCents = centsFromInput(promoPrice);
  const promoQuantityLimit = promoLimit.trim() === "" ? undefined : Number(promoLimit);
  // The API silently treats one without the other as no promo at all — saying
  // so here beats a saved plan that looks configured but does nothing.
  const promoMismatched =
    (promoPriceCents !== undefined) !== (promoQuantityLimit !== undefined);
  const maintenanceGraceMonths = graceMonths.trim() === "" ? undefined : Number(graceMonths);

  // The legacy pair still drives the payment-link path and the rate card, so it
  // is kept coherent with whichever recurring rate exists rather than left to
  // drift away from the two that replaced it.
  const interval =
    monthlyCents !== undefined ? "month" : yearlyCents !== undefined ? "year" : "once";
  const legacyPrice = monthlyCents ?? yearlyCents;

  return (
    <form
      className="space-y-5 border-b border-hairline p-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          // productId cannot be changed once a plan exists — the update
          // endpoint ignores it — and policy has no editor here, so sending
          // DEFAULT_POLICY on every edit would silently reset whatever the
          // plan's real schedule is. Both are create-only.
          ...(isEditing ? {} : { productId, policy: DEFAULT_POLICY }),
          name,
          interval,
          maxActivations,
          currency: "USD",
          requiresLicense,
          selfServe,
          provisioning,
          // On create, an empty field means "no rate yet" and is left out so
          // the plan gets its normal default. On edit, the fields are already
          // showing what the plan has — an empty box here has to mean "take
          // this away", not "leave it as whatever it already was".
          ...(isEditing
            ? {
                priceCents: legacyPrice ?? null,
                oneTimePriceCents: centsFromInput(build) ?? null,
                priceMonthlyCents: monthlyCents ?? null,
                priceYearlyCents: yearlyCents ?? null,
                compareAtPriceCents: compareAtCents ?? null,
                promoPriceCents: promoPriceCents ?? null,
                promoQuantityLimit: promoQuantityLimit ?? null,
                maintenanceGraceMonths: maintenanceGraceMonths ?? null,
                commissionRule,
                floorPriceCents: centsFromInput(floorPrice) ?? null,
                floorMonthlyCents: centsFromInput(floorMonthly) ?? null,
              }
            : {
                ...(legacyPrice === undefined ? {} : { priceCents: legacyPrice }),
                ...(centsFromInput(build) === undefined
                  ? {}
                  : { oneTimePriceCents: centsFromInput(build) }),
                ...(monthlyCents === undefined ? {} : { priceMonthlyCents: monthlyCents }),
                ...(yearlyCents === undefined ? {} : { priceYearlyCents: yearlyCents }),
                ...(compareAtCents === undefined ? {} : { compareAtPriceCents: compareAtCents }),
                ...(promoPriceCents === undefined ? {} : { promoPriceCents }),
                ...(promoQuantityLimit === undefined ? {} : { promoQuantityLimit }),
                ...(maintenanceGraceMonths === undefined ? {} : { maintenanceGraceMonths }),
                ...(commissionRule === null ? {} : { commissionRule }),
                ...(centsFromInput(floorPrice) === undefined
                  ? {}
                  : { floorPriceCents: centsFromInput(floorPrice) }),
                ...(centsFromInput(floorMonthly) === undefined
                  ? {}
                  : { floorMonthlyCents: centsFromInput(floorMonthly) }),
              }),
        });
      }}
    >
      <div className="grid gap-4 md:grid-cols-3">
        <Field label="Product" {...(isEditing ? { hint: "Permanent." } : {})}>
          <Select
            value={productId}
            required
            disabled={isEditing}
            {...(isEditing ? { className: "text-dim" } : {})}
            onChange={(event) => setProductId(event.target.value)}
          >
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Plan name">
          <Input value={name} required onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Activations included">
          <Input
            type="number"
            min={0}
            value={maxActivations}
            required
            onChange={(event) => setMaxActivations(Number(event.target.value))}
          />
        </Field>
      </div>

      <fieldset className="space-y-3 border-0 p-0">
        <legend className="field-label">What it costs</legend>
        <div className="grid gap-4 md:grid-cols-4">
          <Field label="Build" hint="Charged once.">
            <Input
              type="number"
              min={0}
              step="0.01"
              value={build}
              placeholder="0.00"
              onChange={(event) => setBuild(event.target.value)}
            />
          </Field>
          <Field label="Monthly" hint="Maintenance and hosting.">
            <Input
              type="number"
              min={0}
              step="0.01"
              value={monthly}
              placeholder="0.00"
              onChange={(event) => setMonthly(event.target.value)}
            />
          </Field>
          <Field label="Yearly" hint="The other option a buyer picks.">
            <Input
              type="number"
              min={0}
              step="0.01"
              value={yearly}
              placeholder="0.00"
              onChange={(event) => setYearly(event.target.value)}
            />
          </Field>
          <Field
            label="Lowest sale price"
            hint="A marketer cannot sell below this. Blank means list price only."
          >
            <Input
              type="number"
              min={0}
              step="0.01"
              value={floorPrice}
              placeholder="0.00"
              onChange={(event) => setFloorPrice(event.target.value)}
            />
          </Field>
        </div>
        {recurring && (
          <div className="grid gap-4 md:grid-cols-4">
            <Field label="Lowest monthly" hint="The floor on the recurring rate.">
              <Input
                type="number"
                min={0}
                step="0.01"
                value={floorMonthly}
                placeholder="0.00"
                onChange={(event) => setFloorMonthly(event.target.value)}
              />
            </Field>
          </div>
        )}
      </fieldset>

      <CommissionRuleEditor
        {...(plan?.id === undefined ? {} : { planId: plan.id })}
        value={commissionRule}
        listPriceCents={centsFromInput(build)}
        monthlyCents={monthlyCents}
        floorPriceCents={centsFromInput(floorPrice)}
        onChange={setCommissionRule}
      />

      <fieldset className="space-y-3 border-0 p-0">
        <legend className="field-label">Billing start (optional)</legend>
        <div className="grid gap-4 md:grid-cols-4">
          <Field
            label="Maintenance-free months"
            hint="Recurring billing starts this many months after checkout. Leave blank to start immediately."
          >
            <Input
              type="number"
              min={0}
              step="1"
              value={graceMonths}
              placeholder="e.g. 6"
              onChange={(event) => setGraceMonths(event.target.value)}
            />
          </Field>
        </div>
      </fieldset>

      <fieldset className="space-y-3 border-0 p-0">
        <legend className="field-label">Launch pricing (optional)</legend>
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Compare-at" hint="A permanent 'was' figure. Shown only while higher than Build.">
            <Input
              type="number"
              min={0}
              step="0.01"
              value={compareAt}
              placeholder="0.00"
              onChange={(event) => setCompareAt(event.target.value)}
            />
          </Field>
          <Field label="Promo price" hint="Charged instead of Build until the limit is reached.">
            <Input
              type="number"
              min={0}
              step="0.01"
              value={promoPrice}
              placeholder="0.00"
              onChange={(event) => setPromoPrice(event.target.value)}
            />
          </Field>
          <Field label="Promo limit" hint="Paid sales at the promo price before it reverts to Build.">
            <Input
              type="number"
              min={1}
              step="1"
              value={promoLimit}
              placeholder="e.g. 5"
              onChange={(event) => setPromoLimit(event.target.value)}
            />
          </Field>
        </div>
        {promoMismatched && (
          <ErrorNote>
            A promo needs both a promo price and a limit — with only one set, checkout ignores it
            and charges the Build price as usual.
          </ErrorNote>
        )}
      </fieldset>

      <fieldset className="space-y-3 border-0 p-0">
        <legend className="field-label">How it is delivered</legend>
        <div className="grid gap-4 md:grid-cols-2">
          <Field
            label="When the customer gets it"
            hint="A website has to be built first; software exists the moment it is paid for."
          >
            <Select value={provisioning} onChange={(event) => setProvisioning(event.target.value)}>
              <option value="on_payment">At once, when payment clears</option>
              <option value="on_delivery">When we mark the project live</option>
            </Select>
          </Field>

          <div className="space-y-3 self-end">
            <label className="flex items-start gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={requiresLicense}
                className="mt-0.5"
                onChange={(event) => setRequiresLicense(event.target.checked)}
              />
              <span>
                Issues a licence key
                <span className="block text-xs text-dim">
                  Off for hosting and services, which have nothing to unlock.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={selfServe}
                className="mt-0.5"
                onChange={(event) => setSelfServe(event.target.checked)}
              />
              <span>
                Sell this on the website
                <span className="block text-xs text-dim">
                  Anyone can buy it with a card, unattended.
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* The API refuses the purchase anyway; saying so here saves publishing
            something that turns out to be unbuyable at the till. */}
        {selfServe && !recurring && (
          <ErrorNote>
            A plan sold on the website needs a monthly or yearly rate — maintenance is part of
            every sale, and there would be nothing to renew.
          </ErrorNote>
        )}
      </fieldset>

      {!isEditing && (
        <p className="text-sm text-dim">
          The plan starts on the standard schedule: heartbeat every 30 minutes, warn after a week,
          checkout off after two, blocked after three.
        </p>
      )}

      {error !== null && <ErrorNote>{error}</ErrorNote>}

      <Button
        type="submit"
        variant="primary"
        disabled={pending || (selfServe && !recurring) || promoMismatched}
      >
        {pending ? "Saving…" : isEditing ? "Save plan" : "Create plan"}
      </Button>
    </form>
  );
}

/**
 * What's configured, not what's left — the admin list has no live count of
 * paid orders at the promo price, only the plan's own settings. The public
 * site is where "N left" comes from, computed fresh on every request.
 */
function LaunchPriceCell({ plan }: { plan: Plan }) {
  if (plan.promoPriceCents != null && plan.promoQuantityLimit != null) {
    return (
      <span>
        {formatMoney(plan.promoPriceCents, plan.currency)}
        <span className="text-dim"> → {formatMoney(plan.oneTimePriceCents, plan.currency)}</span>
        <span className="text-dim"> after {plan.promoQuantityLimit}</span>
      </span>
    );
  }
  if (plan.compareAtPriceCents != null) {
    return <span className="text-dim">was {formatMoney(plan.compareAtPriceCents, plan.currency)}</span>;
  }
  return <span className="text-dim">—</span>;
}

/**
 * Selling is a separate switch from publishing, and there is nothing to turn on
 * for a plan with no recurring rate — the checkout would take the build fee and
 * commit us to running the thing for nothing.
 */
function SellToggle({
  plan,
  pending,
  onToggle,
}: {
  plan: Plan;
  pending: boolean;
  onToggle: () => void;
}) {
  const sellable =
    (plan.priceMonthlyCents ?? null) !== null || (plan.priceYearlyCents ?? null) !== null;

  if (!plan.selfServe && !sellable) {
    return (
      <span className="text-xs text-dim" title="Add a monthly or yearly rate first">
        No rate
      </span>
    );
  }

  return (
    <Button variant="ghost" disabled={pending} onClick={onToggle}>
      {plan.selfServe ? "On sale" : "Not sold"}
    </Button>
  );
}

/** What each status actually means for who can see and buy the product. */
const STATUS_HINTS: Record<ProductStatus, string> = {
  draft: "Here only. Not on the website.",
  public: "On the website, and sellable.",
  internal: "Ours. Sellable, never shown publicly.",
  disabled: "Retired. Not sold or shown; licences already issued keep working.",
};

/**
 * Only `public` is affirmative, so only it reads as positive. Draft and
 * internal are quiet states rather than problems; disabled is the one worth
 * catching an eye, because a retired product still answering to old licences
 * is easy to forget about.
 */
function ProductStatusBadge({ status }: { status: ProductStatus }) {
  const tone = { public: "live", draft: "quiet", internal: "quiet", disabled: "pending" } as const;
  const label = { public: "Public", draft: "Draft", internal: "Internal", disabled: "Disabled" };
  return <Badge tone={tone[status]}>{label[status]}</Badge>;
}
